import React, { useState } from 'react';
import styles from './add-guest-modal.module.scss';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextErrorMode,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { GuestUiModel } from '../../../../../shared/ui-models/guest.ui-model';
import { FormElementData } from '../../../../../shared/interfaces/form-element-data.interface';
import DatePicker from 'react-datepicker';
import Select, { ValueType } from 'react-select';
import COUNTRIES from './countries.json';

const genderOption = [
  { label: 'Male', value: 'male' },
  { label: 'FeMale', value: 'female' }
];

export const GUEST_VISIT_TYPES = [
  { value: 'business', label: 'Business' },
  {
    value: 'leisure',
    label: 'Leisure'
  },
  { value: 'family_vacation', label: 'Family vacation' },
  {
    value: 'other',
    label: 'Other'
  }
];

export interface AddGuestModalProps {
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    closeHandler: () => void;
    addGuestHandler: (guestData: GuestUiModel) => void;
  };
}

/**
 * functional component AddGuestModal
 * @param {AddGuestModalProps}
 */
export const AddGuestModal: React.FC<AddGuestModalProps> = ({ config, eventHandlers }) => {
  const [newGuestFirstName, setNewGuestFirstName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newGuestLastName, setNewGuestLastName] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newGuestEmail, setNewGuestEmail] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newGuestPhone, setNewGuestPhone] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newAge, setNewAge] = useState<FormElementData<number>>({
    value: 0,
    isValid: false
  });
  const [newGuestAddress, setNewGuestAddress] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newGuestGender, setNewGuestGender] = useState<FormElementData<string>>({
    value: 'male',
    isValid: true
  });
  const [newPurposeOfStay, setNewPurposeOfStay] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newNationality, setNewNationality] = useState<FormElementData<string>>({
    value: '',
    isValid: false
  });
  const [newGuestDob, setNewGuestDob] = useState<FormElementData<Date | null>>({
    value: new Date(),
    isValid: true
  });
  const isMobileView = useWindowResizer();

  const handleNewGuestSubmit = () => {
    const newGuest: GuestUiModel = {
      firstName: newGuestFirstName.value,
      lastName: newGuestLastName.value,
      email: newGuestEmail.value,
      phoneNumber: newGuestPhone.value,
      age: newAge.value,
      address: newGuestAddress.value,
      dob: newGuestDob.value?.toString(),
      gender: newGuestGender.value as any,
      purposeOfStay: newPurposeOfStay?.value,
      nationality: newNationality?.value
    };
    eventHandlers.addGuestHandler(newGuest);
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true }}
      eventHandlers={{
        closeModalHandler: () => {
          eventHandlers.closeHandler();
        }
      }}
    >
      <div className={styles['add-guest-modal']}>
        <div className={styles['add-guest-modal__title']}>Add guest</div>
        <div className={styles['add-guest-modal__description']}>
          First Name
          <InputText
            data={{
              defaultValue: '',
              errorMessage: 'First name is required',
              placeholder: 'First Name'
            }}
            config={{
              maxRows: 1,
              autoFocus: true,
              required: true,
              showErrorMode: InputTextErrorMode.instant
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setNewGuestFirstName({ value, isValid });
              }
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Last Name
          <InputText
            data={{
              defaultValue: '',
              errorMessage: 'Last name is required',
              placeholder: 'Last Name'
            }}
            config={{
              maxRows: 1,
              required: true,
              showErrorMode: InputTextErrorMode.instant
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setNewGuestLastName({ value, isValid });
              }
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Email
          <InputText
            data={{
              defaultValue: '',
              errorMessage: 'Email is required',
              placeholder: 'Email'
            }}
            config={{
              maxRows: 1,
              required: true,
              showErrorMode: InputTextErrorMode.instant
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setNewGuestEmail({ value, isValid });
              }
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Phone Number
          <InputText
            data={{
              defaultValue: '',
              errorMessage: 'Phone number is required',
              placeholder: 'Phone Number'
            }}
            config={{
              maxRows: 1,
              required: true,
              type: InputTextType.text,
              showErrorMode: InputTextErrorMode.instant
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setNewGuestPhone({ value, isValid });
              }
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Age
          <InputText
            data={{
              defaultValue: '',
              errorMessage: 'Age is required',
              placeholder: 'Age'
            }}
            config={{
              maxRows: 1,
              required: true,
              type: InputTextType.number,
              showErrorMode: InputTextErrorMode.instant
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setNewAge({ value: Number(value), isValid });
              }
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Address
          <InputText
            data={{
              defaultValue: '',
              errorMessage: 'Address is required',
              placeholder: 'Address'
            }}
            config={{
              maxRows: 1,
              required: true,
              showErrorMode: InputTextErrorMode.instant
            }}
            eventHandlers={{
              onChangeHandler: (value: string, isValid: boolean) => {
                setNewGuestAddress({ value, isValid });
              }
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Date of Birth
          <DatePicker
            selected={newGuestDob.value}
            onChange={(date) => setNewGuestDob({ value: date as Date, isValid: true })}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          gender
          <Select
            options={genderOption}
            defaultValue={genderOption.find((val) => val.value === newGuestGender.value)}
            onChange={(value: ValueType<{ value: string; label: string }, false>) => {
              setNewGuestGender({ value: value?.value || 'male', isValid: true });
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Purpose of stay
          <Select
            options={GUEST_VISIT_TYPES}
            defaultValue={GUEST_VISIT_TYPES.find((val) => val.value === newPurposeOfStay.value)}
            onChange={(value: ValueType<{ value: string; label: string }, false>) => {
              if (!value) return;

              setNewPurposeOfStay({ value: value?.value, isValid: true });
            }}
          />
        </div>
        <div className={styles['add-guest-modal__description']}>
          Nationality
          <Select
            isSearchable
            options={COUNTRIES}
            defaultValue={COUNTRIES.find((val) => val.value === newNationality.value)}
            onChange={(value: ValueType<{ value: string; label: string }, false>) => {
              if (!value) return;

              setNewNationality({ value: value?.value, isValid: true });
            }}
          />
        </div>
      </div>
      <div className={styles['add-guest-modal--actions']}>
        <Button
          onClick={() => {
            eventHandlers.closeHandler();
          }}
          tracking={{ event: 'record_edit_cancelled', entity: 'guest', method: 'close' }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Cancel
        </Button>
        <Button
          disabled={
            !newGuestFirstName.isValid ||
            !newGuestLastName.isValid ||
            !newGuestEmail.isValid ||
            !newGuestPhone.isValid ||
            !newGuestAddress.isValid ||
            !newGuestGender.isValid ||
            !newGuestDob.isValid ||
            !newPurposeOfStay.isValid ||
            !newNationality.isValid ||
            !newAge.isValid
          }
          onClick={handleNewGuestSubmit}
          tracking={{
            event: 'record_saved',
            entity: 'guest',
            action: 'update',
            outcome: 'success'
          }}
          size={isMobileView ? ButtonSize.full : ButtonSize.medium}
        >
          Add guest
        </Button>
      </div>
    </Modal>
  );
};
